import { FieldTemplate, FormTemplate, LayoutItem } from 'api/models';
import { mutation, query } from 'operations';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  ToastSeverity,
  fieldTemplatesState,
  layoutSelector,
  formTemplateState,
  toastStateSelector,
  formTemplateVersionsState,
} from 'state';
import { GeneralIds, ModelTypes } from 'types/types';
import { getId } from 'utils';
import { FormTemplateStatus } from 'api/models/FormTemplate';

interface SaveFormProps {
  showToast?: boolean;
}
export function useSaveFormTemplate({ showToast = true }: SaveFormProps = {}) {
  const { projectId, formTemplateTypeId } = useParams<GeneralIds>();
  const setToast = useSetRecoilState(toastStateSelector);
  const [formTemplate, setFormTemplate] = useRecoilState(formTemplateState);
  const { t, i18n } = useTranslation();
  const [layout, setLayout] = useRecoilState(layoutSelector);
  const [fieldTemplates, setFieldTemplates] =
    useRecoilState(fieldTemplatesState);
  const [formTemplateVersions, setFormTemplateVersions] = useRecoilState(
    formTemplateVersionsState,
  );

  // the form template is being set by other calls so we need to get the latest version, if it wasn't set yet we use the first fetched one
  const formTemplateId =
    formTemplate?.id ?? formTemplateVersions?.[0]?.id ?? '';
  const formTemplateStatus =
    formTemplate?.status ??
    formTemplateVersions?.[0]?.status ??
    FormTemplateStatus.Draft;

  interface OnSaveProps {
    items?: LayoutItem[];
    templates?: FieldTemplate[];
    finalize?: boolean;
  }
  // Only use props if you need to do a save after/before the state change (e.g. autosaving the form)
  async function onSave(props?: OnSaveProps) {
    setToast({
      isOpen: false,
    });

    const {
      items: propItems,
      templates: propTemplates,
      finalize,
    } = props ?? {};
    // TODO: refactor the props
    let items = propItems ?? layout.children ?? [];
    let templates = propTemplates ?? fieldTemplates ?? [];

    const isLastVersion =
      formTemplateVersions.findIndex(
        version => version.id === formTemplateId,
      ) ===
      formTemplateVersions.length - 1;

    const isNewDraftVersion =
      !finalize &&
      (formTemplateStatus === FormTemplateStatus.Published || !isLastVersion);

    const highestVersion = formTemplateVersions.reduce(
      (acc, version) => (version.version > acc ? version.version : acc),
      1,
    );
    const version = isNewDraftVersion ? highestVersion + 1 : highestVersion;

    if (isNewDraftVersion) {
      function updateItems(items: LayoutItem[]): LayoutItem[] {
        return items.map(item => {
          if (item.fieldTemplateId) {
            const newFieldTemplateId = getId();
            const fieldTemplate = templates.find(
              template => template.id === item.fieldTemplateId,
            );
            if (fieldTemplate) {
              templates = templates.map(template =>
                template.id === fieldTemplate.id
                  ? { ...template, id: newFieldTemplateId }
                  : template,
              );
              return {
                ...item,
                fieldTemplateId: newFieldTemplateId,
                _id: item.id,
              };
            }
          }

          if (item.children) {
            return { ...item, children: updateItems(item.children) };
          }

          return item;
        });
      }
      items = updateItems(items);
    }

    try {
      const { data: template } = await mutation<FormTemplate>({
        type: ModelTypes.formTemplate,
        body: {
          id: isNewDraftVersion ? '' : formTemplateId,
          status: finalize
            ? FormTemplateStatus.Published
            : FormTemplateStatus.Draft,
          layout: {
            items: items,
            options: {
              layout: '',
            },
          },
          fieldTemplates: templates.map(fieldTemplate => ({
            ...fieldTemplate,
            _id: fieldTemplate.id,
            version,
          })),
          version,
          mappings: formTemplate?.mappings,
        },
        ids: {
          id: isNewDraftVersion ? '' : formTemplateId,
          projectId,
          formTemplateTypeId,
        },
      });
      const { data: versions } = await query<FormTemplate[]>({
        type: ModelTypes.formTemplate,
        ids: {
          projectId,
          formTemplateTypeId,
        },
      });

      const latestVersion = [...versions].sort(
        (a, b) => b.version - a.version,
      )[0];

      setFormTemplate(template!);
      setFormTemplateVersions(versions);
      setFieldTemplates(latestVersion.fieldTemplates);
      setLayout({ children: items });

      if (showToast) {
        setToast({
          isOpen: true,
          message: t('general.success.save'),
          severity: ToastSeverity.success,
        });
      }
    } catch (e: any) {
      console.error(e);
      const errorCode = e.response?.data?.error?.[0].code;
      setToast({
        isOpen: true,
        message: i18n.exists(`errors.${errorCode}`)
          ? t(`errors.${errorCode}`)
          : t('general.errors.save'),
        severity: ToastSeverity.error,
      });
    }
  }

  return { save: onSave };
}
